import React, { useState } from 'react';
import { Card } from 'antd';
import { momentForDomain } from '@common/services/moment-shadow';
import { Colors } from '@common/tokens';
import { Icon, Gutter, Text, Col, Row } from '@common/components';
import { formatNumberDigits } from '@common/services';
import { toHumanTimeFormat } from 'works/legacy/lib/date-helper';
import type { PreviewDataType } from './_transform-simulation-data-service';
import clsx from 'clsx';

const moment = momentForDomain('scheduling', 'shifts');

type ShiftGroupType = PreviewDataType['shiftGroups'][number];
type ShiftChildType = ShiftGroupType['children'][number];

// date-helper is dayjs-backed post-M5.1 cutover and dayjs has no objectSupport,
// so the old `{ hour }` construction is an Invalid Date. Build each time with the
// domain shadow moment (valid in both libs — no object construction) instead.
function formatHourRange(startHour: number, endHour: number, duration: number) {
  const start = toHumanTimeFormat(moment().startOf('day').hour(startHour));
  const end = toHumanTimeFormat(moment().startOf('day').hour(endHour));
  return `${start} - ${end} (${formatNumberDigits(duration)} hrs)`;
}

type ShiftStatusType = ShiftGroupType['status'];

function buildDiffLabel(newCount: number, diffCount: number) {
  if (newCount === 0) {
    return '(shift removed)';
  }

  return diffCount > 0 ? `(+${diffCount})` : `(${diffCount})`;
}

function OpeningsDisplay({
  status,
  prevCount,
  newCount,
  diffCount,
}: {
  status: ShiftStatusType;
  prevCount: number;
  newCount: number;
  diffCount: number;
}) {
  if (status === 'new') {
    return <Text weight="light">Openings: {newCount}</Text>;
  }

  const diffColor = diffCount < 0 ? 'coral' : 'meadowDarker';
  const diffLabel = buildDiffLabel(newCount, diffCount);

  return (
    <Text weight="light">
      Openings: {prevCount} → {newCount}{' '}
      <Text color={diffColor}>{diffLabel}</Text>
    </Text>
  );
}

function ShiftChildCard({ child }: { child: ShiftChildType }) {
  return (
    <Card className="shift-preview-card--child">
      <Row>
        <Col span={16}>
          <Row align="middle" className="shift-title">
            <Icon
              icon={child.icon === 'moon' ? 'moon' : 'sun'}
              color={child.icon === 'moon' ? 'skyRegular' : 'harvestGold'}
            />
            <Gutter size="sm" direction="left">
              <Text size="lg" weight="medium">
                {formatHourRange(
                  child.startHour,
                  child.endHour,
                  child.duration
                )}
              </Text>
            </Gutter>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col flex="auto">
          <Row className="shift-details">
            <Text>Unit: {child.unitName}</Text>
          </Row>
          <Row className="shift-details">
            <Text>Location: {child.locationName}</Text>
          </Row>
          <Row className="shift-details">
            <Text>Role: {child.roleName}</Text>
          </Row>
          <Row className="shift-details">
            <OpeningsDisplay
              status={child.status}
              prevCount={child.prevCount}
              newCount={child.openings}
              diffCount={child.diffCount}
            />
          </Row>
        </Col>
      </Row>
    </Card>
  );
}

// TODO: [WORK-5054] Refactor ShiftPreviewCard and ShiftChildCard to use our Card component
// instead of antd Card, and extract nested card pattern as a reusable common component.
export function ShiftPreviewCard({ group }: { group: ShiftGroupType }) {
  const expandable = group.children.length > 0;
  const [open, setOpen] = useState(expandable);

  return (
    <>
      <Card
        className={clsx(
          'shift-preview-card',
          expandable && 'shift-preview-card--expandable'
        )}
        onClick={expandable ? () => setOpen((prev) => !prev) : undefined}
      >
        <Row>
          {expandable && (
            <Col>
              <Gutter size="xs" direction="right">
                <Icon
                  icon={open ? 'chevronDown' : 'chevronRight'}
                  size="md"
                  color="elm"
                />
              </Gutter>
            </Col>
          )}
          <Col span={16}>
            <Row align="middle" className="shift-title">
              <Icon
                icon={group.icon === 'moon' ? 'moon' : 'sun'}
                color={group.icon === 'moon' ? 'skyRegular' : 'harvestGold'}
              />
              <Gutter size="sm" direction="left">
                <Text size="lg" weight="medium">
                  {formatHourRange(
                    group.startHour,
                    group.endHour,
                    group.duration
                  )}
                </Text>
              </Gutter>
            </Row>
            <Row className="shift-details">
              <Text weight="light">Unit: {group.unitName}</Text>
            </Row>
            <Row className="shift-details">
              <Text weight="light">Location: {group.locationName}</Text>
            </Row>
            <Row className="shift-details">
              <Text weight="light">Role: {group.roleName}</Text>
            </Row>
            <Row className="shift-details">
              <OpeningsDisplay
                status={group.status}
                prevCount={group.prevCount}
                newCount={group.headcount}
                diffCount={group.diffCount}
              />
            </Row>
            {expandable && !open && (
              <Gutter size="md" direction="top">
                <Text weight="light" color="midnightRegular">
                  {group.children.length} partial{' '}
                  {group.children.length === 1 ? 'shift' : 'shifts'}
                </Text>
              </Gutter>
            )}
          </Col>
        </Row>
        {open &&
          group.children.map((child) => (
            <ShiftChildCard key={child.key} child={child} />
          ))}
      </Card>
      <style jsx>{`
        :global(.shift-preview-card) {
          border: 1px solid ${Colors.steel};
          border-radius: 8px;
          margin-bottom: 16px;
        }
        :global(.shift-preview-card--expandable) {
          border-left-width: 4px;
        }
        :global(.shift-preview-card--expandable > .ant-card-body) {
          cursor: pointer;
          padding-left: 12px;
        }
        :global(.shift-preview-card .shift-title) {
          padding-bottom: 16px;
        }
        :global(.shift-preview-card .shift-details .ant-typography) {
          color: ${Colors.midnightRegular};
        }
        :global(.shift-preview-card--child) {
          border: 1px solid ${Colors.steel};
          border-radius: 8px;
          margin-left: 4px;
          margin-top: 12px;
        }
        :global(.shift-preview-card--child .shift-title) {
          padding-bottom: 16px;
        }
        :global(.shift-preview-card--child .shift-details .ant-typography) {
          color: ${Colors.midnightRegular};
        }
      `}</style>
    </>
  );
}
